(function () {
  'use strict';

  const { gsap } = window;
  const { iman, BaseCarousel, layout } = window.Universe;

  class UgcCarousel {
    constructor(section) {
      if (section) {
        this.init(section);
      }
    }

    init(section) {
      this.DOM = {};
      this.DOM.section = section;
      this.sectionId = section.closest('.shopify-section')?.id || null;
      this.DOM.slides = this.DOM.section.querySelectorAll('.js-ugc-carousel-slide');

      if (this.DOM.slides.length) {
        this.DOM.prev = this.DOM.section.querySelectorAll('.js-carousel-prev');
        this.DOM.next = this.DOM.section.querySelectorAll('.js-carousel-next');

        this.activeIndex = layout.device >= 1 ? 0 : 1;

        this.onChangeSlide = () => {
          const nextIndex = this.instance.instance.selectedScrollSnap();
          this.activeIndex = nextIndex;

          this.updateSlideOffsets();
          this.updateLiveRegion();
          this.updateButtonStates();
          this.updateMobileVideoPlayback();
        };

        this.onPrev = () => {
          this.instance.instance.scrollPrev();
        };

        this.onNext = () => {
          this.instance.instance.scrollNext();
        };

        this.setup();
      }
    }

    setup() {
      this.updateSlideOffsets();

      const hasAutoplay = 'autoplay' in this.DOM.section.dataset;
      const delay = parseFloat(this.DOM.section.dataset.autoplayDelay) || 3;

      this.instance = new BaseCarousel({
        wrap: this.DOM.section,
        carouselOptions: {
          containScroll: 'trimSnaps',
          breakpoints: {
            '(min-width: 1024px)': {
              align: 'start',
              inViewThreshold: 0.3,
              watchDrag: false,
            },
            '(max-width: 1023px)': {
              align: 'start',
              watchDrag: true,
            },
            '(max-width: 767px)': {
              align: 'center',
              watchDrag: true,
            },
          },
        },
        withAutoPlay: hasAutoplay,
        autoPlayDelay: delay * 1000,
        onReady: () => {
          this.instance.instance.on('select', this.onChangeSlide);

          if (this.DOM.prev.length) {
            for (let i = 0; i < this.DOM.prev.length; i++) {
              this.DOM.prev[i].addEventListener('click', this.onPrev);
            }
          }

          if (this.DOM.next.length) {
            for (let i = 0; i < this.DOM.next.length; i++) {
              this.DOM.next[i].addEventListener('click', this.onNext);
            }
          }

          this.setupCards();
          this.setupAccessibility();
          this.updateMobileVideoPlayback();
          this.setupDeferredAutoplay();
        },
      });
    }

    setupAccessibility() {
      this.setupKeyboardNavigation();
      this.setupLiveRegion();
      this.handleReducedMotion();
      this.updateButtonStates();
    }

    setupKeyboardNavigation() {
      this.onKeyDown = (e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }

        switch (e.key) {
          case 'ArrowRight':
            e.preventDefault();
            this.instance.instance.scrollNext();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            this.instance.instance.scrollPrev();
            break;
          case 'Home':
            e.preventDefault();
            this.instance.instance.scrollTo(0);
            break;
          case 'End':
            e.preventDefault();
            this.instance.instance.scrollTo(this.DOM.slides.length - 1);
            break;
          default:
            break;
        }
      };

      this.DOM.viewport = this.DOM.section.querySelector('.js-carousel');
      if (this.DOM.viewport) {
        this.DOM.viewport.addEventListener('keydown', this.onKeyDown);
      }
    }

    setupLiveRegion() {
      let liveRegion = this.DOM.section.querySelector('#carousel-live-region');

      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'carousel-live-region';
        liveRegion.className = 'sr-only';
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');

        const carouselBody = this.DOM.section.querySelector('.ugc-carousel__body');
        if (carouselBody) {
          carouselBody.insertBefore(liveRegion, carouselBody.firstChild);
        }
      }

      this.DOM.liveRegion = liveRegion;
      this.updateLiveRegion();
    }

    updateLiveRegion() {
      if (this.DOM.liveRegion) {
        const currentSlide = this.activeIndex + 1;
        const totalSlides = this.DOM.slides.length;
        const template = window.ugcCarouselStrings?.slideAnnouncement || 'Slide [current] of [total]';
        this.DOM.liveRegion.textContent = template.replace('[current]', currentSlide).replace('[total]', totalSlides);
      }
    }

    handleReducedMotion() {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (prefersReducedMotion) {
        if (this.instance && this.instance.autoplay) {
          this.instance.autoplay.stop();
        }
      }
    }

    updateButtonStates() {
      if (!this.instance || !this.instance.instance) return;

      const canScrollPrev = this.instance.instance.canScrollPrev();
      const canScrollNext = this.instance.instance.canScrollNext();

      if (this.DOM.prev.length) {
        for (let i = 0; i < this.DOM.prev.length; i++) {
          this.DOM.prev[i].removeAttribute('disabled');
          this.DOM.prev[i].setAttribute('aria-disabled', !canScrollPrev);
        }
      }

      if (this.DOM.next.length) {
        for (let i = 0; i < this.DOM.next.length; i++) {
          this.DOM.next[i].removeAttribute('disabled');
          this.DOM.next[i].setAttribute('aria-disabled', !canScrollNext);
        }
      }
    }

    setupCards() {
      this.DOM.cards = this.DOM.section.querySelectorAll('.js-media-card');

      this.DOM.cards.forEach((card) => {
        const cardVideo = card.querySelector('.js-media-video');
        const cardVideoPoster = card.querySelector('.js-media-poster');

        const playBtn = card.querySelector('.js-media-play-btn');
        if (playBtn) {
          const handlePlayClick = () => {
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const poster = card.querySelector('.js-media-poster');
            const targets = [poster, playBtn].filter(Boolean);

            const fadeAndRemove = () => {
              if (gsap && !prefersReducedMotion) {
                gsap.to(targets, {
                  autoAlpha: 0,
                  duration: 0.35,
                  ease: 'power2.out',
                  onComplete: () => targets.forEach((el) => el?.remove()),
                });
              } else {
                targets.forEach((el) => el?.remove());
              }
            };

            if (cardVideo) {
              cardVideo.play().catch(() => {});
              fadeAndRemove();
            } else {
              const { videoType, videoId, videoLoop } = playBtn.dataset;
              const loop = videoLoop === 'true';

              let src = '';
              if (videoType === 'youtube') {
                src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=${loop ? 1 : 0}&playlist=${videoId}&controls=0&rel=0&modestbranding=1&playsinline=1`;
              } else if (videoType === 'vimeo') {
                src = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=${loop ? 1 : 0}&background=1&playsinline=1`;
              }

              if (!src) return;

              const iframe = document.createElement('iframe');
              iframe.width = '100%';
              iframe.height = '100%';
              iframe.src = src;
              iframe.title = poster?.querySelector('img')?.alt || 'Video';
              iframe.setAttribute('frameborder', '0');
              iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
              iframe.style.cssText = 'pointer-events: none !important;';

              const mediaContainer = card.querySelector('.media-card__media');
              if (!mediaContainer) return;

              mediaContainer.appendChild(iframe);
              fadeAndRemove();
            }

            playBtn.removeEventListener('click', handlePlayClick);
          };

          playBtn.addEventListener('click', handlePlayClick);
        }

        if (card.dataset.autoplay === 'false' && cardVideo && !playBtn) {
          const handleMouseEnter = () => {
            if (cardVideoPoster) {
              gsap.to(cardVideoPoster, {
                autoAlpha: 0,
                duration: 0.5,
                ease: 'power4.inOut',
              });
            }

            cardVideo.play();
          };

          const handleMouseLeave = () => {
            if (cardVideoPoster) {
              gsap.to(cardVideoPoster, {
                autoAlpha: 1,
                duration: 0.5,
                ease: 'power4.inOut',
              });
            }

            cardVideo.pause();
          };

          card.addEventListener('mouseenter', handleMouseEnter);
          card.addEventListener('mouseleave', handleMouseLeave);
          card._eventlisteners = { handleMouseEnter: handleMouseEnter, handleMouseLeave: handleMouseLeave };
        }
      });
    }

    setupDeferredAutoplay() {
      const hasDeferred =
        this.DOM.section.querySelector('[data-autoplay="deferred"]') ||
        this.DOM.section.querySelector('.js-deferred-video');

      if (!hasDeferred) return;

      this.deferredObserver = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;

          const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

          if (!prefersReducedMotion) {
            this.DOM.section.querySelectorAll('[data-autoplay="deferred"] .js-media-video').forEach((video) => {
              video.play().catch(() => {});
              const poster = video.closest('.js-media-card')?.querySelector('.js-media-poster');
              if (poster) poster.style.opacity = '0';
            });

            this.DOM.section.querySelectorAll('.js-deferred-video').forEach((placeholder) => {
              const { videoType, videoId, videoLoop } = placeholder.dataset;
              const loop = videoLoop === 'true';
              let src = '';

              if (videoType === 'youtube') {
                src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=${loop ? 1 : 0}&playlist=${videoId}&controls=0&rel=0&modestbranding=1&playsinline=1`;
              } else if (videoType === 'vimeo') {
                src = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=${loop ? 1 : 0}&background=1&playsinline=1`;
              }

              if (!src) return;

              const iframe = document.createElement('iframe');
              iframe.width = '100%';
              iframe.height = '100%';
              iframe.src = src;
              iframe.setAttribute('frameborder', '0');
              iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
              iframe.style.cssText = 'pointer-events: none !important;';
              placeholder.replaceWith(iframe);
            });
          }

          this.deferredObserver.disconnect();
          this.deferredObserver = undefined;
        },
        { threshold: 0.1 }
      );

      this.deferredObserver.observe(this.DOM.section);
    }

    updateMobileVideoPlayback() {
      if (!window.matchMedia('(max-width: 767px)').matches) return;

      this.DOM.slides.forEach((slide) => {
        const video = slide.querySelector('.js-media-video');
        if (!video) return;

        if (slide.classList.contains('is-snapped')) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    updateSlideOffsets() {
      this.DOM.section.classList.toggle('ugc-carousel--odd-active', this.activeIndex % 2 !== 0);
    }

    resize() {
      if (this.DOM) {
        if (layout.device > 1) {
          // desktop
        } else {
          // mobile/tablet
        }
      }
    }

    destroy() {
      if (this.DOM) {
        if (this.DOM.prev.length) {
          for (let i = 0; i < this.DOM.prev.length; i++) {
            this.DOM.prev[i].removeEventListener('click', this.onPrev);
          }
        }

        if (this.DOM.next.length) {
          for (let i = 0; i < this.DOM.next.length; i++) {
            this.DOM.next[i].removeEventListener('click', this.onNext);
          }
        }

        if (this.DOM.viewport && this.onKeyDown) {
          this.DOM.viewport.removeEventListener('keydown', this.onKeyDown);
        }

        if (this.deferredObserver) {
          this.deferredObserver.disconnect();
          this.deferredObserver = undefined;
        }

        this.DOM.cards.forEach((card) => {
          if (card._eventlisteners) {
            const listeners = card._eventlisteners;

            if (listeners.handleMouseEnter) card.removeEventListener('mouseenter', listeners.handleMouseEnter);
            if (listeners.handleMouseLeave) card.removeEventListener('mouseleave', listeners.handleMouseLeave);
          }
        });

        if (this.instance.instance) {
          this.instance.instance.off('select', this.onChangeSlide);
        }
        if (this.instance) {
          this.instance.destroy();
        }

        this.instance = undefined;
        this.DOM = undefined;
      }
    }
  }

  class UgcCarousels {
    constructor() {
      this.init();
    }

    setup() {
      for (let i = 0; i < this.DOM.sections.length; i++) {
        this.carousels.push(new UgcCarousel(this.DOM.sections[i]));
      }
    }

    reinit() {
      this.destroy();
      this.init();
    }

    reinitSection(sectionEl) {
      // Distruggi solo i caroselli dentro questa sezione
      this.destroySection(sectionEl);

      // Null-guard PRIMA di pushare
      if (!this.DOM) {
        this.DOM = {};
        this.carousels = [];
      }

      // Trova nuovi elementi nella sezione ri-renderizzata
      const newSections = sectionEl.querySelectorAll('.js-ugc-carousel-section');
      for (let i = 0; i < newSections.length; i++) {
        this.carousels.push(new UgcCarousel(newSections[i]));
      }

      // Aggiorna DOM.sections
      this.DOM.sections = document.querySelectorAll('.js-ugc-carousel-section');
    }

    destroySection(sectionEl) {
      if (!this.carousels) return;

      // Matching per section ID, NON per DOM containment (nodi vecchi sono stale)
      const targetId = sectionEl.id || sectionEl.closest('.shopify-section')?.id;
      if (!targetId) return;

      for (let i = this.carousels.length - 1; i >= 0; i--) {
        const carousel = this.carousels[i];
        if (carousel.sectionId === targetId) {
          carousel.destroy();
          this.carousels.splice(i, 1);
        }
      }
    }

    onBlockSelect(blockEl) {
      // Trova il carosello che contiene questo blocco e scrolla alla slide
      // blockEl è un NUOVO nodo DOM, quindi DOM containment funziona qui
      for (let i = 0; i < this.carousels.length; i++) {
        const carousel = this.carousels[i];
        if (carousel.DOM && carousel.DOM.section.contains(blockEl)) {
          const slide = blockEl.closest('.js-ugc-carousel-slide');
          if (!slide) break;

          const slideIndex = Array.from(carousel.DOM.slides).indexOf(slide);
          if (slideIndex !== -1 && carousel.instance && carousel.instance.instance) {
            const autoplay = carousel.instance.instance.plugins().autoplay;
            if (autoplay) autoplay.stop();
            carousel.instance.instance.scrollTo(slideIndex);
          }
          break;
        }
      }
    }

    onBlockDeselect(blockEl) {
      for (let i = 0; i < this.carousels.length; i++) {
        const carousel = this.carousels[i];
        if (carousel.DOM && carousel.DOM.section.contains(blockEl)) {
          if (carousel.instance && carousel.instance.instance) {
            const autoplay = carousel.instance.instance.plugins().autoplay;
            if (autoplay) autoplay.play();
          }
          break;
        }
      }
    }

    destroy() {
      if (this.DOM) {
        for (let i = 0; i < this.carousels.length; i++) {
          this.carousels[i].destroy();
        }
        this.carousels = [];
        this.DOM = undefined;
      }
    }

    init(container = document) {
      const sections = container.querySelectorAll('.js-ugc-carousel-section');

      if (sections.length) {
        this.DOM = {};
        this.DOM.sections = sections;
        this.carousels = [];
        this.setup();
      }
    }
  }

  function init() {
    if (!iman.get('ugcCarousels')) {
      iman.add('ugcCarousels', new UgcCarousels());
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Universe.UgcCarousels = UgcCarousels;
})();
